import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./Dashboard.module.scss";
import { Container } from "../common/Layout";
import MainCard from "../common/MainCard";
import { DashboardMetricCard } from "../Cards";
import { useGlobalContext } from "../../GlobalContext";
import ModalsSection from "../ModalsSection";
import PageWrap from "../common/PageWrap";
import RecentRides from "../tables/RecentRides";
import RidesTable from "../tables/RidesTable";
import Button from "../common/Button";
import NetworkService from "../../NetworkService";
import moment from "moment";
import CreditValueEditorModal from "../modals/CreditValueEditorModal";
import { useNavigate } from "react-router-dom";
import { formatPrice, refetchCompanyData, toasterFunction } from "../utils";
import { Message } from "rsuite";
import { ROUTES } from "../../constants/routes";
import { RideListResponse } from "../../types/ride";
import { CompanyData, isSpecialCompany } from "../../types/company";
import useRideCancelNotifications from "../hooks/useRideCancelNotifications";

const ORDER_OPTIONS = [
  {
    type: "now",
    title: "Order Ride Now",
    description: "Immediate pickup for your destination",
    icon: (
      <></>
    ),
  },
  {
    type: "schedule",
    title: "CAR:GO Elit",
    description: "Book a ride for a specific date and time",
    icon: (
      <></>
    ),
  },
] as const;

type CreditModalState = { modalType: "add-credit" | "edit-limit" } | null;

const RECENT_SERVICES_REFETCH_MS = 20000;

const EMPTY_TODAYS_RIDES: RideListResponse = {
  data: [],
  total: 0,
  current_page: 1,
  per_page: 15,
};

function toRideListResponse(rides: unknown): RideListResponse {
  const data = Array.isArray(rides) ? rides : (rides as RideListResponse)?.data ?? [];

  return {
    ...EMPTY_TODAYS_RIDES,
    data,
    total: data.length,
    per_page: data.length || EMPTY_TODAYS_RIDES.per_page,
  };
}

async function loadDashboardRides(
  companyData: CompanyData,
  companyAdminId?: number,
): Promise<RideListResponse> {
  if (isSpecialCompany(companyData)) {
    if (!companyAdminId) return EMPTY_TODAYS_RIDES;

    return toRideListResponse(
      await NetworkService.getUpcomingScheduledRides(companyAdminId),
    );
  }

  return NetworkService.getRides(
    moment().startOf("day").format("YYYY-MM-DD"),
    moment().format("YYYY-MM-DD"),
  );
}

function Dashboard() {
  const { user: userState, companyData: companyDataState } = useGlobalContext();
  const [user] = userState;
  const [companyData, setCompanyData] = companyDataState;
  const [openCreditEditModal, setOpenCreditEditModal] = useState<CreditModalState>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [todaysRides, setTodaysRides] = useState<RideListResponse | null>(null);
  const isCompanyDataLoading = !companyData;
  const isSpecial = isSpecialCompany(companyData);

  const navigate = useNavigate();

  const editLimit = () => {
    setOpenCreditEditModal({
      modalType: "edit-limit",
    });
  };

  const companyAdminId = user?.user?.id;
  const activeRides = useMemo(() => todaysRides?.data ?? [], [todaysRides]);

  const handleRideCanceled = useCallback((ride: { id: number | string }) => {
    toasterFunction(
      <Message full closable showIcon type="error">
        Service #{ride.id} was canceled.
      </Message>,
      {
        duration: 20000
      }
    );
  }, []);

  useRideCancelNotifications(activeRides, {
    companyAdminId,
    enabled: isSpecial && !!companyAdminId,
    onCanceled: handleRideCanceled,
  });

  const fetchTodaysRides = useCallback(async (showLoading = true) => {
    if (!user || !NetworkService.jwtToken || !companyData) return;

    if (showLoading) setLoading(true);

    try {
      setTodaysRides(await loadDashboardRides(companyData, companyAdminId));
    } catch {
      if (showLoading) setTodaysRides(EMPTY_TODAYS_RIDES);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [companyAdminId, companyData, user]);

  useEffect(() => {
    if (!companyData) return;

    fetchTodaysRides(true);
  }, [companyData, fetchTodaysRides]);

  useEffect(() => {
    if (!isSpecial) return;

    const intervalId = window.setInterval(() => {
      fetchTodaysRides(true);
    }, RECENT_SERVICES_REFETCH_MS);

    return () => window.clearInterval(intervalId);
  }, [isSpecial, fetchTodaysRides]);

  useEffect(() => {
    if (!isSpecial || window.location.hash !== `#${ROUTES.RECENT_SERVICES_HASH}`) return;

    const timer = window.setTimeout(() => {
      document.getElementById(ROUTES.RECENT_SERVICES_HASH)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);

    return () => window.clearTimeout(timer);
  }, [isSpecial]);

  const changeCompanyLimit = async (credit: number) => {
    await NetworkService.editCompanyCreditLowLimit(credit);
    setOpenCreditEditModal(null);
    toasterFunction(
      <Message closable type="success">
        Company limit successfully changed.
      </Message>,
    );
    refetchCompanyData(setCompanyData);
  };

  return (
    <PageWrap>
      {openCreditEditModal && (
        <CreditValueEditorModal
          header="Change company credit low limit"
          btnLabel="Set new limit"
          subHeader="Limit in RSD"
          onSubmit={(credit: number) => changeCompanyLimit(credit)}
          open={!!openCreditEditModal}
          initialLimitValue={companyData?.company?.credit_low_limit}
          close={() => setOpenCreditEditModal(null)}
        />
      )}
      <Container className={styles.dashboard}>
        <div className={styles.greetingsContainer}>
          <div className={styles.header}>Hey {user?.user?.name},check your account:</div>

          <div className={styles.description}>
            Welcome,{companyData?.company?.name}
            <br />
            Here, in one place, you can find information about the status and consumption for your company
          </div>
        </div>

        {isSpecial && (
          <div className={styles.orderRideSection}>
            <div className={styles.orderRideSectionTitle}>Book a ride</div>
            <div className={styles.orderRideCards}>
              {ORDER_OPTIONS.map((opt) => (
                <button
                  key={opt.type}
                  className={styles.orderRideCard}
                  onClick={() => navigate(`${ROUTES.ORDER_RIDE}?type=${opt.type}`)}
                >
                  <div className={styles.orderRideIcon}>{opt.icon}</div>
                  <div className={styles.orderRideCardTitle}>{opt.title}</div>
                  <div className={styles.orderRideCardDesc}>{opt.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={styles.orderRideSectionTitle}>Company info</div>
        <div className={styles.cardsGrid}>
          <div className={styles.mainCardItem}>
            <MainCard className={styles.mainCardCol} />
          </div>
          <DashboardMetricCard
            className={styles.cardCurrentBalance}
            onClick={undefined}
            cardClassName={`${styles.dashboardMetricCardInner} ${
              isCompanyDataLoading ? styles.dashboardLoadingCard : ""
            }`}
            header={"Current balance:"}
            value={isCompanyDataLoading ? "" : formatPrice(companyData?.company?.credit)}
          />
          <DashboardMetricCard
            className={styles.cardLowLimit}
            onClick={() => editLimit()}
            cardClassName={`${styles.dashboardMetricCardInner} ${
              isCompanyDataLoading ? styles.dashboardLoadingCard : ""
            }`}
            header={"Company credit low limit:"}
            value={isCompanyDataLoading ? "" : formatPrice(companyData?.company?.credit_low_limit)}
            button={
              <Button
                className={styles.editLimitButton}
                variant="white"
                onClick={() => editLimit()}
                disabled={isCompanyDataLoading}>
                Edit
              </Button>
            }
          />
          <DashboardMetricCard
            className={styles.cardTodayRides}
            onClick={undefined}
            cardClassName={`${styles.dashboardMetricCardInner} ${
              isCompanyDataLoading ? styles.dashboardLoadingCard : ""
            }`}
            header={"Today rides:"}
            value={isCompanyDataLoading ? "" : companyData?.ridesToday || 0}
          />
          <DashboardMetricCard
            className={styles.cardTodaySpent}
            onClick={undefined}
            cardClassName={`${styles.dashboardMetricCardInner} ${
              isCompanyDataLoading ? styles.dashboardLoadingCard : ""
            }`}
            header={"Today money spent: "}
            value={isCompanyDataLoading ? "" : formatPrice(companyData?.expenseToday)}
          />
        </div>
        <ModalsSection />

        {!isCompanyDataLoading &&
          (isSpecial ? (
            <>
              <div id={ROUTES.RECENT_SERVICES_HASH} className={styles.tableHeaderWrap}>
                <div className={styles.tableHeader}>Recent services:</div>
                <Button
                  onClick={() => {
                    navigate(ROUTES.SERVICES);
                  }}
                  className={styles.showAllButton}
                  variant="white">
                  View All
                </Button>
              </div>
              <RecentRides
                pagination={{}}
                hidePagination={true}
                loading={loading}
                data={todaysRides ?? EMPTY_TODAYS_RIDES}
                onPaginationChange={() => {}}
                onRidesChange={() => fetchTodaysRides(true)}
              />
            </>
          ) : (
            <>
              <div className={styles.tableHeaderWrap}>
                <div className={styles.tableHeader}>Todays last services:</div>
                <Button
                  onClick={() => {
                    navigate(ROUTES.SERVICES);
                  }}
                  className={styles.showAllButton}
                  variant="white">
                  View All
                </Button>
              </div>
              <RidesTable
                pagination={{}}
                hidePagination={true}
                loading={loading}
                data={todaysRides}
                onPaginationChange={() => {}}
              />
            </>
          ))}
      </Container>
    </PageWrap>
  );
}

export default Dashboard;
